import { ReactElement, useEffect, useMemo, useState } from 'react'
import { buildErrorMessage } from '@constants'
import { AxiosError } from 'axios'
import { toNumber } from 'lodash-es'
import TuneIcon from '@mui/icons-material/Tune'
import {
  Alert,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import { getGuidToStorey } from '@editor/components/Controls/IFCUploadWidget/utils/ifcMetadataUtils'
import { storeyToLabel } from '@editor/pages/Plans/components/AddPlanDialog/components/utils'
import { useMutation } from '@tanstack/react-query'
import { PopoverPanel } from '@ui2/structure'
import { ImportButton } from '@ui/actions'
import { enqueueSnackbar } from '@ui/snackbar'
import { useControlStore, useModelStore } from '@editorStores'
import { useIfcModelMetadata } from '@editorHooks'
import { useModelInvalidation } from '@structuralPlanningHooks'
import {
  IFCWallParseReport,
  startIfcWallParse,
  StoreyAssignmentStrategy,
  StoreyDetectStrategyType,
  WallPlacement,
} from '@mutations'
import { getWallParseWarning } from './ifcParseReport'

/**
 * Storey selection modes offered in the form. The two `StoreyDetectStrategyType` values map
 * directly to a backend strategy; `as-configured` is a frontend-only mode that derives a per-element
 * strategy from the storey assignment made in the "Stockwerkszuweisung" step.
 */
type StoreySelectionMode = StoreyDetectStrategyType | 'as-configured'

interface Props {
  guids: string[]
  ifcFileId: string | null
  projectId: string
  onSuccess: () => void
  /** Inner (`Internal`) vs. outer (`External`) wall. Defaults to `Internal`. */
  placement?: WallPlacement
  /**
   * Per-element placement, used when the selection mixes inner and outer walls (the guided "Wände
   * importieren" step derives it from the type assignment). Elements missing here fall back to
   * `placement`.
   */
  placementByGuid?: Record<string, WallPlacement>
}

/** Numeric storeys from storey_boundaries, sorted ascending. */
const storeyOptionsFromBoundaries = (
  storeyBoundaries: Record<string, [number, number]>,
): string[] =>
  Object.keys(storeyBoundaries)
    .map(s => toNumber(s))
    .filter(n => !Number.isNaN(n))
    .sort((a, b) => b - a)
    .map(n => n.toString())

const DEFAULT_MIN_MATCH_LENGTH = 1.0
const DEFAULT_MAX_SHORTENING_DISTANCE = 0.15
const DEFAULT_MIN_OVERLAP_FOR_DUPLICATE_DETECTION = 0.5

const IFCParseAsWallForm = ({
  guids,
  ifcFileId,
  projectId,
  onSuccess,
  placement = 'Internal',
  placementByGuid,
}: Props): ReactElement => {
  const storeyBoundaries = useModelStore(state => state.model.storey_boundaries)
  const invalidateModel = useModelInvalidation()
  const activeIfcFileGuid = useControlStore(state => state.activeIfcFileGuid)
  const metadata = useIfcModelMetadata(activeIfcFileGuid)
  const guidToStorey = useMemo(
    () => getGuidToStorey(metadata?.storeyAssignments),
    [metadata?.storeyAssignments],
  )
  // Selected elements that have no (numeric) storey assignment — these fall back to auto-detect in
  // `as-configured` mode, so we warn about them.
  const unassignedGuidCount = useMemo(
    () => guids.filter(guid => Number.isNaN(toNumber(guidToStorey[guid]))).length,
    [guids, guidToStorey],
  )
  const storeyOptions = useMemo(
    () => storeyOptionsFromBoundaries(storeyBoundaries),
    [storeyBoundaries],
  )
  const [storeySelectionMode, setStoreySelectionMode] =
    useState<StoreySelectionMode>('as-configured')
  const [targetStorey, setTargetStorey] = useState<string>(() =>
    storeyOptions.length > 0 ? storeyOptions[0] : '',
  )
  const [parseOpenings, setParseOpenings] = useState(true)
  const [enforceCartesianAlignment, setEnforceCartesianAlignment] = useState(true)
  const [limitOpeningXYCoords, setLimitOpeningXYCoords] = useState(true)
  const [shortenWallOnImport, setShortenWallOnImport] = useState(true)
  const [maxShorteningDistance, setMaxShorteningDistance] = useState(
    DEFAULT_MAX_SHORTENING_DISTANCE,
  )
  const [handleDuplicateWallConflicts, setHandleDuplicateWallConflicts] = useState(true)
  const [minOverlapForDuplicateDetection, setMinOverlapForDuplicateDetection] = useState(
    DEFAULT_MIN_OVERLAP_FOR_DUPLICATE_DETECTION,
  )
  const [minMatchLength, setMinMatchLength] = useState(DEFAULT_MIN_MATCH_LENGTH)
  const [allowMultipleMatches, setAllowMultipleMatches] = useState(false)
  const [optionsAnchorEl, setOptionsAnchorEl] = useState<HTMLElement | null>(null)

  useEffect(() => {
    if (storeyOptions.length === 0) {
      setTargetStorey('')
    } else if (!storeyOptions.includes(targetStorey)) {
      setTargetStorey(storeyOptions[0])
    }
  }, [storeyOptions, targetStorey])

  // Per-element storey strategy. For `as-configured` we reuse the storey assigned in the
  // "Stockwerkszuweisung" step; elements without a (numeric) assignment fall back to auto-detect
  // with the default minimum match length.
  const buildStoreyAssignmentStrategy = (guid: string): StoreyAssignmentStrategy => {
    if (storeySelectionMode === 'provide-target-storey') {
      return { storeyDetectStrategyType: 'provide-target-storey', storey: toNumber(targetStorey) }
    }

    if (storeySelectionMode === 'as-configured') {
      const assignedStorey = toNumber(guidToStorey[guid])
      if (!Number.isNaN(assignedStorey)) {
        return { storeyDetectStrategyType: 'provide-target-storey', storey: assignedStorey }
      }
      return {
        storeyDetectStrategyType: 'auto-detect-storey',
        minMatchLength: DEFAULT_MIN_MATCH_LENGTH,
        allowMultipleMatches: false,
      }
    }

    return { storeyDetectStrategyType: 'auto-detect-storey', minMatchLength, allowMultipleMatches }
  }

  const { mutate: parseAsWall, isLoading: isParsing } = useMutation({
    mutationFn: async () => {
      if (!projectId || !ifcFileId || guids.length === 0) return
      const configs = guids.map(ifc_element_id => ({
        ifc_element_id,
        ifc_file_id: ifcFileId,
        parsing_target_type: 'wall' as const,
        storeyAssignmentStrategy: buildStoreyAssignmentStrategy(ifc_element_id),
        parseOpenings,
        enforceCartesianAlignment,
        limitOpeningXYCoords,
        placement: placementByGuid?.[ifc_element_id] ?? placement,
        shortenWallOnImport,
        maxShorteningDistance,
        handleDuplicateWallConflicts,
        minOverlapForDuplicateDetection,
      }))
      return startIfcWallParse.request(projectId, configs) as Promise<
        IFCWallParseReport | undefined
      >
    },
    onSuccess: async (report?: IFCWallParseReport) => {
      if (projectId) await invalidateModel(projectId)
      const warning = getWallParseWarning(report)
      if (warning) enqueueSnackbar(warning, { variant: 'warning' })
      else
        enqueueSnackbar(`${guids.length > 1 ? 'Wände' : 'Wand'} erfolgreich importiert`, {
          variant: 'success',
        })
      onSuccess()
    },
    onError: async (error: AxiosError) => {
      enqueueSnackbar(
        buildErrorMessage(error, 'Fehler bei der Wandimportierung. Bitte Modell kontrollieren.'),
        {
          variant: 'error',
        },
      )
      if (projectId) await invalidateModel(projectId)
    },
  })

  const canParse = Boolean(
    projectId &&
      ifcFileId &&
      guids.length > 0 &&
      storeyOptions.length > 0 &&
      (!shortenWallOnImport || maxShorteningDistance > 0) &&
      (!handleDuplicateWallConflicts || minOverlapForDuplicateDetection > 0) &&
      (storeySelectionMode === 'auto-detect-storey'
        ? minMatchLength > 0
        : storeySelectionMode === 'provide-target-storey'
        ? targetStorey !== ''
        : true),
  )

  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
        <Typography>Stockwerk</Typography>
        <FormControl sx={{ minWidth: 200 }}>
          <Select
            value={storeySelectionMode}
            variant="outlined"
            onChange={e => setStoreySelectionMode(e.target.value as StoreySelectionMode)}
            disabled={isParsing}
            size="small"
            sx={{ '& .MuiSelect-select': { py: 0.25, fontSize: '0.75rem' } }}
          >
            <MenuItem value="as-configured">wie konfiguriert / automatisch</MenuItem>
            <MenuItem value="auto-detect-storey">automatisch ermitteln</MenuItem>
            <MenuItem value="provide-target-storey">angeben</MenuItem>
          </Select>
        </FormControl>
      </Stack>
      {storeySelectionMode === 'as-configured' && (
        <Stack spacing={1}>
          <Typography variant="body2" color="text.secondary">
            Verwendet die Stockwerkszuweisung aus dem Zuweisungsschritt. Nicht zugewiesene Elemente
            werden automatisch ermittelt (min. {DEFAULT_MIN_MATCH_LENGTH} m).
          </Typography>
          {unassignedGuidCount > 0 && (
            <Alert severity="warning" sx={{ py: 0 }}>
              {unassignedGuidCount === 1
                ? '1 ausgewähltes Element hat keine Stockwerkszuweisung und wird automatisch ermittelt.'
                : `${unassignedGuidCount} von ${guids.length} ausgewählten Elementen haben keine Stockwerkszuweisung und werden automatisch ermittelt.`}
            </Alert>
          )}
        </Stack>
      )}
      {storeySelectionMode === 'provide-target-storey' && (
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="space-between"
          padding={0}
        >
          <Typography fontSize="inherit" noWrap>
            Zielstockwerk
          </Typography>
          <FormControl sx={{ minWidth: 100 }}>
            <Select
              id="ifc-element-form-storey"
              value={targetStorey}
              variant="outlined"
              onChange={e => setTargetStorey(e.target.value)}
              disabled={storeyOptions.length === 0 || isParsing}
              size="small"
              sx={{ '& .MuiSelect-select': { py: 0.25, fontSize: '0.75rem' } }}
            >
              {storeyOptions.map(storey => (
                <MenuItem key={storey} value={storey}>
                  {storeyToLabel(storey)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      )}
      {storeySelectionMode === 'auto-detect-storey' && (
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="space-between"
          padding={0}
        >
          <Typography fontSize="inherit" noWrap>
            Mindesthöhe im SW für Zuweisung
          </Typography>
          <TextField
            size="small"
            type="number"
            variant="standard"
            value={minMatchLength}
            onChange={e => setMinMatchLength(Number(e.target.value))}
            disabled={isParsing}
            inputProps={{ min: 0, step: 0.1 }}
            InputProps={{
              endAdornment: <InputAdornment position="end">m</InputAdornment>,
            }}
            sx={{ maxWidth: '100px' }}
          />
        </Stack>
      )}
      {storeySelectionMode === 'auto-detect-storey' && (
        <FormControlLabel
          control={
            <Switch
              checked={allowMultipleMatches}
              onChange={e => setAllowMultipleMatches(e.target.checked)}
              disabled={isParsing}
              size="small"
            />
          }
          label="Elemente zu mehreren SW zuordnen"
        />
      )}
      <Stack direction="row" spacing={1}>
        <ImportButton
          variant="contained"
          onClick={() => parseAsWall()}
          disabled={!canParse}
          loading={isParsing}
          size="small"
          sx={{ flex: 1 }}
        >
          {guids.length > 1 ? `${guids.length} Wände importieren` : 'Als Wand importieren'}
        </ImportButton>
        <IconButton
          size="small"
          onClick={event => setOptionsAnchorEl(event.currentTarget)}
          disabled={isParsing}
          sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}
          data-cy="btn-ifc-wall-import-options"
        >
          <TuneIcon fontSize="small" />
        </IconButton>
      </Stack>
      <PopoverPanel
        anchorEl={optionsAnchorEl}
        onClose={() => setOptionsAnchorEl(null)}
        title="Import-Optionen"
        dataCy="ifc-wall-import-options-popover"
      >
        <FormControlLabel
          control={
            <Switch
              checked={parseOpenings}
              onChange={e => setParseOpenings(e.target.checked)}
              disabled={isParsing}
              size="small"
            />
          }
          label="Öffnungen importieren"
        />
        <FormControlLabel
          control={
            <Switch
              checked={limitOpeningXYCoords}
              onChange={e => setLimitOpeningXYCoords(e.target.checked)}
              disabled={isParsing}
              size="small"
            />
          }
          label="Öffnungen auf 2 Dez. begrenzen (XY)"
        />
        <FormControlLabel
          control={
            <Switch
              checked={enforceCartesianAlignment}
              onChange={e => setEnforceCartesianAlignment(e.target.checked)}
              disabled={isParsing}
              size="small"
            />
          }
          label="Wände kartesisch ausrichten"
        />
        <FormControlLabel
          control={
            <Switch
              checked={shortenWallOnImport}
              onChange={e => setShortenWallOnImport(e.target.checked)}
              disabled={isParsing}
              size="small"
            />
          }
          label="Automatisch verkürzen"
        />
        {shortenWallOnImport && (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="space-between"
            padding={0}
          >
            <Typography fontSize="inherit" noWrap>
              Max. Verkürzung
            </Typography>
            <TextField
              size="small"
              type="number"
              variant="standard"
              value={maxShorteningDistance}
              onChange={e => setMaxShorteningDistance(Number(e.target.value))}
              disabled={isParsing}
              inputProps={{ min: 0, step: 0.01 }}
              InputProps={{
                endAdornment: <InputAdornment position="end">m</InputAdornment>,
              }}
              sx={{ maxWidth: '100px' }}
            />
          </Stack>
        )}
        <FormControlLabel
          control={
            <Switch
              checked={handleDuplicateWallConflicts}
              onChange={e => setHandleDuplicateWallConflicts(e.target.checked)}
              disabled={isParsing}
              size="small"
            />
          }
          label="Doppelte Wände vermeiden"
        />
        {handleDuplicateWallConflicts && (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="space-between"
            padding={0}
          >
            <Typography fontSize="inherit" noWrap>
              Min. Überlappung
            </Typography>
            <TextField
              size="small"
              type="number"
              variant="standard"
              value={minOverlapForDuplicateDetection}
              onChange={e => setMinOverlapForDuplicateDetection(Number(e.target.value))}
              disabled={isParsing}
              inputProps={{ min: 0, step: 0.1 }}
              InputProps={{
                endAdornment: <InputAdornment position="end">m</InputAdornment>,
              }}
              sx={{ maxWidth: '100px' }}
            />
          </Stack>
        )}
      </PopoverPanel>
      {storeyOptions.length === 0 && (
        <Typography variant="caption" color="text.secondary">
          Keine Stockwerke im Modell. Bitte zuerst Stockwerksgrenzen definieren.
        </Typography>
      )}
    </Stack>
  )
}

export default IFCParseAsWallForm
